import * as React from "react";
import { Backdrop, CircularProgress, Grid, Stack } from "@mui/material";
import PatientInformation from "./PatientInformation";
import PrimaryInsurance from "./PrimaryInsurance";
import Organization from "./Organization";
import Payer from "./Payer";
import ChargeLine from "./ChargeLine";
import FinancialSummary from "./FinancialSummary";
import SecondaryInsurance from "./SecondaryInsurance";
import { useParams, useHistory, useLocation } from "react-router-dom";
import { getHeaders } from "../../utils/HeaderHelper";
import { AuthenticationContext } from "../../Context";
import {
  PatientType,
  PrimaryInsuranceType,
  SecondaryInsuranceType,
} from "../types";
import ClaimBuilderStepTwoHeader from "./ClaimBuilderStepTwoHeader";
import BillingProvider from "./BillingProvider";
import { useFlash } from "../../FlashContext";

export const emptyPatient: PatientType = {
  id: "",
  first_name: "",
  middle_name: "",
  last_name: "",
  address_line_1: "",
  address_line_2: "",
  city: "",
  state: "",
  zip: "",
  zipExtension: "",
  date_of_birth: null,
  gender: "",
};

export const emptyPrimaryInsurance: PrimaryInsuranceType = {
  id: "",
  insurance_type: "",
  plan_name: "",
  insurance_number: "",
  first_name: "",
  middle_name: "",
  last_name: "",
  address_line_1: "",
  address_line_2: "",
  city: "",
  state: "",
  zip: "",
  insured_dob: null,
  gender: "",
  relationship: "",
  phone_number: "",
};

export const emptySecondaryInsurance: SecondaryInsuranceType = {
  id: "",
  insurance_type: "",
  plan_name: "",
  insurance_number: "",
  first_name: "",
  middle_name: "",
  last_name: "",
  address_line_1: "",
  address_line_2: "",
  city: "",
  state: "",
  zip: "",
  insured_dob: null,
  gender: "",
  relationship: "",
  phone_number: "",
};

const ClaimsBuilderStepTwo: React.FC = () => {
  const { id: patientId, claimId } = useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const encounterBillingId = queryParams.get("encounterBillingId");

  const authenticationSetting = React.useContext(AuthenticationContext);
  const { showToast } = useFlash();

  const history = useHistory();

  const [loading, setLoading] = React.useState<any>(false);

  const [patient, setPatient] = React.useState<PatientType>(emptyPatient);
  const [primaryInsurance, setPrimaryInsurance] =
    React.useState<PrimaryInsuranceType>(emptyPrimaryInsurance);
  const [secondaryInsurance, setSecondaryInsurance] =
    React.useState<SecondaryInsuranceType>(emptySecondaryInsurance);
  const [insuranceTypeOptions, setInsuranceTypeOptions] =
    React.useState<any>(null);
  const [claim, setClaim] = React.useState<any>(null);
  const [organization, setOrganization] = React.useState<any>(null);
  const [billingProvider, setBillingProvider] = React.useState<any>(null);
  const [patientValid, setPatientValid] = React.useState<boolean>(false);
  const [primaryInsuranceValid, setPrimaryInsuranceValid] =
    React.useState<boolean>(false);
  const [secondaryInsuranceValid, setSecondaryInsuranceValid] =
    React.useState<boolean>(false);
  const [organizationValid, setOrganizationValid] =
    React.useState<boolean>(false);
  const [billingProviderValid, setBillingProviderValid] =
    React.useState<boolean>(false);
  const [patientEditing, setPatientEditing] = React.useState(false);
  const [primaryInsuranceEditing, setPrimaryInsuranceEditing] =
    React.useState(false);
  const [secondaryInsuranceEditing, setSecondaryInsuranceEditing] =
    React.useState(false);

  const isSecondaryInsuranceRequired =
    !!secondaryInsurance || !!claim?.opt_out_sec_ins;

  const isStepTwoValid =
    patientValid &&
    primaryInsuranceValid &&
    organizationValid &&
    primaryInsurance?.insurance_type &&
    claim?.total_diagnosis_codes <= 8 &&
    (!isSecondaryInsuranceRequired || secondaryInsuranceValid);

  const claimStatus = claim?.claim_status;

  const getClaimStepTwoInfo = () => {
    fetch(`/billing/claims/${claimId}/step_two_info?patient_id=${patientId}`, {
      method: "GET",
      headers: getHeaders(authenticationSetting.csrfToken),
    })
      .then((result) => result.json())
      .then((result) => {
        if (!result.success) {
          console.log(result.error);
        } else {
          const normalizedZip =
            result.resource.patient.zip?.replace(/-/g, "") ?? "";

          setPatient({
            ...result.resource.patient,
            zip: normalizedZip ? normalizedZip.slice(0, 5) : "",
            zipExtension:
              normalizedZip && normalizedZip.length === 9
                ? normalizedZip.slice(5)
                : "",
          });
          setClaim(result.resource.claim);
          setPrimaryInsurance(result.resource?.primary_insurance);
          setSecondaryInsurance(result.resource?.secondary_insurance);
          setInsuranceTypeOptions(result.resource?.insurance_types);
          setOrganization(result.resource?.organization);
          setBillingProvider(result.resource?.billing_provider);
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  React.useEffect(() => {
    getClaimStepTwoInfo();
  }, [claimId, patientId]);

  const handlePatientChange = (updated: Partial<PatientType>) => {
    setPatient((prev) => ({ ...prev, ...updated }));
  };

  const handlePrimaryInsuranceChange = (
    updated: Partial<PrimaryInsuranceType>
  ) => {
    setPrimaryInsurance((prev) => ({ ...prev, ...updated }));
  };

  const handleSecondaryInsuranceChange = (
    updated: Partial<SecondaryInsuranceType>
  ) => {
    setSecondaryInsurance((prev) => ({ ...prev, ...updated }));
  };

  const savePatientSnapshot = async () => {
    const endpoint = `/billing/claims/${claimId}/patient_snapshot?patient_id=${patientId}`;

    const method = "POST";

    let zipCode = "";
    let zip = patient?.zip?.trim();
    let zipExtension = patient?.zipExtension?.trim();
    zipCode = (zip + zipExtension).trim();

    const patientSnapshot = {
      last_name: patient.last_name,
      first_name: patient.first_name,
      middle_name: patient.middle_name,
      address_line_1: patient.address_line_1,
      address_line_2: patient.address_line_2,
      city: patient.city,
      state: patient.state,
      zip: zipCode,
      dob: patient.date_of_birth,
      sex: patient.gender,
      phone: patient.mobile_phone_number,
    };

    const response = await fetch(endpoint, {
      method: method,
      headers: {
        ...getHeaders(authenticationSetting.csrfToken),
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        patient_snapshot: patientSnapshot,
      }),
    });

    const data = await response.json();
    return data;
  };

  const handlePatientSave = async (event) => {
    event.preventDefault();

    if (patient) {
      if (!patient.last_name) {
        showToast({
          title: "Last Name Required",
          text: "Last Name is Required",
          type: "error",
        });
        return;
      }

      if (!patient.first_name) {
        showToast({
          title: "First Name Required",
          text: "First Name is Required",
          type: "error",
        });
        return;
      }

      if (!patient.address_line_1) {
        showToast({
          title: "Address Line One Required",
          text: "Address Line One is Required",
          type: "error",
        });
        return;
      }

      if (!patient.city) {
        showToast({
          title: "City Required",
          text: "City is Required",
          type: "error",
        });
        return;
      }

      if (!patient.state) {
        showToast({
          title: "State Required",
          text: "State is Required",
          type: "error",
        });
        return;
      }

      if (!patient.zip) {
        showToast({
          title: "Zip Code Required",
          text: "Zip Code is Required",
          type: "error",
        });
        return;
      }

      if (!patient.date_of_birth) {
        showToast({
          title: "Date of Birth Required",
          text: "Date of Birth is Required",
          type: "error",
        });
        return;
      }

      if (!patient.mobile_phone_number) {
        showToast({
          title: "Phone Number Required",
          text: "Phone Number is Required",
          type: "error",
        });
        return;
      }

      if (!patient.gender) {
        showToast({
          title: "Sex Required",
          text: "Sex is Required",
          type: "error",
        });
        return;
      }

      try {
        setLoading(true);
        const data = await savePatientSnapshot();

        if (data.success) {
          showToast({
            title: "Patient Snapshot Saved",
            text: "Patient Snapshot has been successfully saved",
            type: "success",
          });
          setLoading(false);
          setPatientEditing(false);
        } else {
          showToast({
            title: "Error",
            text: "Something went wrong",
            type: "error",
          });
          setLoading(false);
        }
      } catch (error) {
        showToast({
          title: "Error",
          text: "Something went wrong",
          type: "error",
        });
        setLoading(false);
      } finally {
        setLoading(false);
      }
    }
  };

  const savePrimaryInsurance = async () => {
    const endpoint = `/billing/claims/${claimId}/insurance_snapshot?patient_id=${patientId}`;

    const method = "POST";

    const primaryInsuranceSnapshot = {
      last_name: primaryInsurance.last_name,
      first_name: primaryInsurance.first_name,
      middle_name: primaryInsurance.middle_name,
      address_line_1: primaryInsurance.address_line_1,
      address_line_2: primaryInsurance.address_line_2,
      city: primaryInsurance.city,
      state: primaryInsurance.state,
      zip: primaryInsurance.zip,
      dob: primaryInsurance.insured_dob,
      sex: primaryInsurance.gender,
      phone: primaryInsurance.phone_number,
      payor: primaryInsurance.insurance_type,
      plan_name: primaryInsurance.plan_name,
      id_number: primaryInsurance.insurance_number,
      relationship: primaryInsurance.relationship,
    };

    const response = await fetch(endpoint, {
      method: method,
      headers: {
        ...getHeaders(authenticationSetting.csrfToken),
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        primary_insurance_snapshot: primaryInsuranceSnapshot,
      }),
    });

    const data = await response.json();
    return data;
  };

  const handlePrimaryInsuranceSave = async (event) => {
    event.preventDefault();

    if (primaryInsurance) {
      const allowedTypes = [
        "ND Medicaid",
        "ND Blue Shield Blue Cross",
        "ND Sanford",
      ];
      const { insurance_type } = primaryInsurance;

      if (!insurance_type) {
        showToast({
          title: "Insurance Type Required",
          text: "Insurance Type is Required",
          type: "error",
        });
        return;
      }

      if (!allowedTypes.includes(insurance_type)) {
        showToast({
          title: "Invalid Insurance Type",
          text: `"${insurance_type}" is not a valid insurance type. Valid options are: [${allowedTypes.join(
            ", "
          )}]`,
          type: "error",
        });
        return;
      }

      if (!primaryInsurance.plan_name) {
        showToast({
          title: "Plan Name Required",
          text: "Insurance Plan is Required",
          type: "error",
        });
        return;
      }

      if (!primaryInsurance.insurance_number) {
        showToast({
          title: "Insurance Number Required",
          text: "Insurance Number is Required",
          type: "error",
        });
        return;
      }

      if (!primaryInsurance.relationship) {
        showToast({
          title: "Relationship Required",
          text: "Relationship is Required",
          type: "error",
        });
        return;
      }

      if (!primaryInsurance.last_name) {
        showToast({
          title: "Last Name Required",
          text: "Insured's Last Name is Required",
          type: "error",
        });
        return;
      }

      if (!primaryInsurance.first_name) {
        showToast({
          title: "First Name Required",
          text: "Insured's First Name is Required",
          type: "error",
        });
        return;
      }

      if (!primaryInsurance.address_line_1) {
        showToast({
          title: "Address Line One Required",
          text: "Address Line One is Required",
          type: "error",
        });
        return;
      }

      if (!primaryInsurance.city) {
        showToast({
          title: "City Required",
          text: "City is Required",
          type: "error",
        });
        return;
      }

      if (!primaryInsurance.state) {
        showToast({
          title: "State Required",
          text: "State is Required",
          type: "error",
        });
        return;
      }

      if (!primaryInsurance.zip) {
        showToast({
          title: "Zip Code Required",
          text: "Zip Code is Required",
          type: "error",
        });
        return;
      }

      if (!primaryInsurance.insured_dob) {
        showToast({
          title: "Date of Birth Required",
          text: "Date of Birth is Required",
          type: "error",
        });
        return;
      }

      if (!primaryInsurance.phone_number) {
        showToast({
          title: "Phone Number Required",
          text: "Phone Number is Required",
          type: "error",
        });
        return;
      }

      if (!primaryInsurance.gender) {
        showToast ({
          title: "Sex Required",
          text: "Sex is Required",
          type: "error",
        });
        return;
      }

      try {
        setLoading(true);
        const data = await savePrimaryInsurance();
        if (data.success) {
          showToast({
            title: "Primary Insurance Snapshot Saved",
            text: "Primary Insurance Snapshot has been successfully saved",
            type: "success",
          });
          setLoading(false);
          setPrimaryInsuranceEditing(false);
        } else {
          showToast({
            title: "Error",
            text: "Something went wrong",
            type: "error",
          });
          setLoading(false);
        }
      } catch (error) {
        showToast({
          title: "Error",
          text: "Something went wrong",
          type: "error",
        });
        setLoading(false);
      } finally {
        setLoading(false);
      }
    }
  };

  const saveSecondaryInsuranceSnapshot = async () => {
    const endpoint = `/billing/claims/${claimId}/secondary_insurance_snapshot?patient_id=${patientId}`;

    const method = "POST";

    const secondaryInsuranceSnapshot = {
      last_name: secondaryInsurance.last_name,
      first_name: secondaryInsurance.first_name,
      middle_name: secondaryInsurance.middle_name,
      address_line_1: secondaryInsurance.address_line_1,
      address_line_2: secondaryInsurance.address_line_2,
      city: secondaryInsurance.city,
      state: secondaryInsurance.state,
      zip: secondaryInsurance.zip,
      dob: secondaryInsurance.insured_dob,
      sex: secondaryInsurance.gender,
      phone: secondaryInsurance.phone_number,
      payor: secondaryInsurance.insurance_type,
      plan_name: secondaryInsurance.plan_name,
      id_number: secondaryInsurance.insurance_number,
      relationship: secondaryInsurance.relationship,
    };

    const response = await fetch(endpoint, {
      method: method,
      headers: {
        ...getHeaders(authenticationSetting.csrfToken),
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        secondary_insurance_snapshot: secondaryInsuranceSnapshot,
      }),
    });

    const data = await response.json();
    return data;
  };

  const handleSecondaryInsuranceSave = async (event) => {
    event.preventDefault();

    if (secondaryInsurance) {
      const allowedTypes = [
        "ND Medicaid",
        "ND Blue Shield Blue Cross",
        "ND Sanford",
      ];
      const { insurance_type } = secondaryInsurance;

      if (!insurance_type) {
        showToast({
          title: "Insurance Type Required",
          text: "Insurance Type is Required",
          type: "error",
        });
        return;
      }

      if (!allowedTypes.includes(insurance_type)) {
        showToast({
          title: "Invalid Insurance Type",
          text: `"${insurance_type}" is not a valid insurance type. Valid options are: ${allowedTypes.join(
            ", "
          )}`,
          type: "error",
        });
        return;
      }

      if (!secondaryInsurance.plan_name) {
        showToast({
          title: "Insurance Plan Required",
          text: "Insurance Plan is Required",
          type: "error",
        });
        return;
      }

      if (!secondaryInsurance.insurance_number) {
        showToast({
          title: "Insurance Number Required",
          text: "Insurance Number is Required",
          type: "error",
        });
        return;
      }

      if (!secondaryInsurance.relationship) {
        showToast({
          title: "Relationship Required",
          text: "Relationship is Required",
          type: "error",
        });
        return;
      }

      if (!secondaryInsurance.last_name) {
        showToast({
          title: "Last Name Required",
          text: "Insured's Last Name is Required",
          type: "error",
        });
        return;
      }

      if (!secondaryInsurance.first_name) {
        showToast({
          title: "First Name Required",
          text: "Insured's First Name is Required",
          type: "error",
        });
        return;
      }

      if (!secondaryInsurance.address_line_1) {
        showToast({
          title: "Address Required",
          text: "Address Line One is Required",
          type: "error",
        });
        return;
      }

      if (!secondaryInsurance.city) {
        showToast({
          title: "City Required",
          text: "City is Required",
          type: "error",
        });
        return;
      }

      if (!secondaryInsurance.state) {
        showToast({
          title: "State Required",
          text: "State is Required",
          type: "error",
        });
        return;
      }

      if (!secondaryInsurance.zip) {
        showToast({
          title: "Zip Code Required",
          text: "Zip Code is Required",
          type: "error",
        });
        return;
      }

      if (!secondaryInsurance.insured_dob) {
        showToast({
          title: "Date of Birth Required",
          text: "Date of Birth is Required",
          type: "error",
        });
        return;
      }

      if (!secondaryInsurance.phone_number) {
        showToast({
          title: "Phone Number Required",
          text: "Phone Number is Required",
          type: "error",
        });
        return;
      }

      if (!secondaryInsurance.gender) {
        showToast({
          title: "Sex Required",
          text: "Sex is Required",
          type: "error",
        });
        return;
      }

      try {
        setLoading(true);

        const data = await saveSecondaryInsuranceSnapshot();

        if (data.success) {
          showToast({
            title: "Secondary Insurance Snapshot Saved",
            text: "Secondary Insurance Snapshot saved",
            type: "success",
          });
          setLoading(false);
          setSecondaryInsuranceEditing(false);
        } else {
          showToast({
            title: "Error",
            text: "Something went wrong",
            type: "error",
          });
          setLoading(false);
        }
      } catch (error) {
        showToast({
          title: "Error",
          text: "Something went wrong",
          type: "error",
        });
        setLoading(false);
      } finally {
        setLoading(false);
      }
    }
  };

  const saveOrganizationSnapshot = async () => {
    const endpoint = `/billing/claims/${claimId}/organization_snapshot`;

    const method = "POST";

    const response = await fetch(endpoint, {
      method: "POST",
      headers: {
        ...getHeaders(authenticationSetting.csrfToken),
        "Content-Type": "application/json",
      },
    });

    const data = await response.json();
    return data;
  };

  const saveBillingProviderSnapshot = async () => {
    const endpoint = `/billing/claims/${claimId}/billing_provider_snapshot`;

    const method = "POST";

    const response = await fetch(endpoint, {
      method: "POST",
      headers: {
        ...getHeaders(authenticationSetting.csrfToken),
        "Content-Type": "application/json",
      },
    });

    const data = await response.json();
    return data;
  };

  const handleClaimSubmit = async () => {
    setLoading(true);
    try {
      const endpoint = `/billing/claims/${claim.id}/submit?patient_id=${patientId}`;
      const method = "POST";

      await savePatientSnapshot();
      await savePrimaryInsurance();
      isSecondaryInsuranceRequired && (await saveSecondaryInsuranceSnapshot());
      await saveOrganizationSnapshot();
      await saveBillingProviderSnapshot();

      const response = await fetch(endpoint, {
        method,
        headers: {
          ...getHeaders(authenticationSetting.csrfToken),
          "Content-Type": "application/json",
        },
      });

      const data = await response.json();

      if (data.success) {
        showToast({
          title: "Claim Submitted",
          text: "Claim submitted successfully",
          type: "success",
        });
        setClaim(data.resource);
        // return data.resource.id || claim?.id || null;
      } else {
        showToast({
          title: "Submission Failed",
          text: "There was a problem submitting the claim. Don't worry — we will retry again on your behalf!",
          type: "error",
        });
        // return null;
      }
    } catch (error) {
      showToast({
        title: "Submission Failed",
        text: "Something went wrong",
        type: "error",
      });
      // return null;
    } finally {
      setLoading(false);
      history.push(
        `/patient_reports/${patientId}/patient_encounters/new?encounter_id=${encounterBillingId}&view_only=1`
      );
    }
  };

  const downloadFile = async () => {
    try {
      window.open(`/billing/claims/${claim.id}/export`, "_blank");
    } catch (error) {
      console.error("Download error:", error);
    } finally {
    }
  };

  const handleDownloadFile = async () => {
    setLoading(true);
    try {
      await savePatientSnapshot();
      await savePrimaryInsurance();
      if (isSecondaryInsuranceRequired) {
        await saveSecondaryInsuranceSnapshot();
      }
      await saveOrganizationSnapshot();
      await saveBillingProviderSnapshot();
      await downloadFile();
    } catch (error) {
      console.error("Download failed:", error);

      showToast({
        title: "Download Failed",
        text: error?.message || "An unexpected error occurred during download.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Stack>
      <ClaimBuilderStepTwoHeader
        claimNumber={claim?.claim_number}
        claimType={claim?.claim_type}
        handleClaimSubmit={handleClaimSubmit}
        isStepTwoValid={isStepTwoValid}
        claimStatus={claimStatus}
        handleDownloadFile={handleDownloadFile}
      />

      <Grid container className="step-two-panel-container">
        <Grid item xs={12}>
          <Grid container className="panel-show-container">
            <Grid container className="panel-information-container">
              <Grid container direction="row" className="admin-header">
                <Grid item xs={12} className="box-header">
                  <Stack
                    direction={"row"}
                    justifyContent={"space-between"}
                    paddingX={3}
                    paddingY={1}
                    alignItems={"center"}
                    display={"flex"}
                  >
                    <Grid item>
                      <p className="panel-header">Claim Review</p>
                    </Grid>
                  </Stack>
                </Grid>
              </Grid>

              <Grid container className="general-claim-info-container">
                <Grid
                  item
                  xs={12}
                  display="flex"
                  flexDirection="column"
                  rowGap={2.5}
                >
                  <PatientInformation
                    patient={patient}
                    handlePatientChange={handlePatientChange}
                    handlePatientSave={handlePatientSave}
                    setErrorStatus={setPatientValid}
                    claimStatus={claimStatus}
                    isEditing={patientEditing}
                    setIsEditing={setPatientEditing}
                  />
                  <PrimaryInsurance
                    primaryInsurance={primaryInsurance}
                    insuranceTypeOptions={insuranceTypeOptions}
                    handlePrimaryInsuranceChange={handlePrimaryInsuranceChange}
                    handlePrimaryInsuranceSave={handlePrimaryInsuranceSave}
                    setErrorStatus={setPrimaryInsuranceValid}
                    claimStatus={claimStatus}
                    isEditing={primaryInsuranceEditing}
                    setIsEditing={setPrimaryInsuranceEditing}
                  />
                  {isSecondaryInsuranceRequired && (
                    <SecondaryInsurance
                      secondaryInsurance={secondaryInsurance}
                      insuranceTypeOptions={insuranceTypeOptions}
                      handleSecondaryInsuranceChange={
                        handleSecondaryInsuranceChange
                      }
                      handleSecondaryInsuranceSave={
                        handleSecondaryInsuranceSave
                      }
                      setErrorStatus={setSecondaryInsuranceValid}
                      claimStatus={claimStatus}
                      isEditing={secondaryInsuranceEditing}
                      setIsEditing={setSecondaryInsuranceEditing}
                    />
                  )}
                  <Organization
                    organization={organization}
                    billingProvider={billingProvider}
                    setErrorStatus={setOrganizationValid}
                    claimStatus={claimStatus}
                  />
                  {billingProvider && (
                    <BillingProvider
                      organization={organization}
                      billingProvider={billingProvider}
                      setErrorStatus={setBillingProviderValid}
                      claimStatus={claimStatus}
                    />
                  )}
                  <Payer payer={primaryInsurance?.insurance_type} />
                  <ChargeLine claim={claim} />
                  <FinancialSummary
                    totalCharges={claim?.total_amount}
                    amountPaid={claim?.amount_paid}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Backdrop open={loading} style={{ zIndex: 9999, color: "#fff" }}>
        <CircularProgress />
      </Backdrop>
    </Stack>
  );
};

export default ClaimsBuilderStepTwo;
